import { useEffect, useRef } from 'react';
import { UserEvent } from '../constants/matrixEvents';
import { useMatrixClient } from '../context/MatrixContext';
import logger from '../utils/logger';

/**
 * Hook to listen for Matrix user avatar changes
 * 
 * This hook sets up an event listener on the Matrix User object
 * and calls the provided callback when the avatar URL changes.
 * Properly cleans up listeners on unmount or when userId changes.
 * 
 * @param {string} userId - Matrix user ID to monitor
 * @param {Function} onAvatarChange - Callback to invoke when avatar changes
 * 
 * @example
 * ```javascript
 * const userId = '@user:matrix.org';
 * const handleAvatarChange = () => {
 *   console.log('Avatar changed!');
 *   loadNewAvatar();
 * };
 * 
 * useMatrixAvatarListener(userId, handleAvatarChange);
 * ```
 */
export const useMatrixAvatarListener = (userId, onAvatarChange) => {
  const client = useMatrixClient();
  const listenerSetupRef = useRef(false);
  const userIdRef = useRef(null);
  const handlerRef = useRef(null);

  useEffect(() => {
    // Early return if no client or userId
    if (!client || !userId) {
      logger.debug('useMatrixAvatarListener: No client or userId, skipping setup');
      return;
    }

    // Check if we're already set up for this exact user
    if (listenerSetupRef.current && userIdRef.current === userId) {
      logger.debug(`useMatrixAvatarListener: Already setup for ${userId}, skipping`);
      return;
    }

    // If we're switching users, clean up the old listener first
    if (listenerSetupRef.current && userIdRef.current && userIdRef.current !== userId) {
      logger.debug(`useMatrixAvatarListener: Switching from ${userIdRef.current} to ${userId}`);
      const oldUser = client.getUser(userIdRef.current);
      if (oldUser && handlerRef.current) {
        oldUser.off(UserEvent.AvatarUrl, handlerRef.current);
        logger.debug(`useMatrixAvatarListener: Cleaned up listener for ${userIdRef.current}`);
      }
      listenerSetupRef.current = false;
      handlerRef.current = null;
    }

    // Get the Matrix user object
    const user = client.getUser(userId);
    if (!user) {
      logger.warn(`useMatrixAvatarListener: Could not get Matrix user object for ${userId}`);
      return;
    }

    // Create the event handler
    const handleAvatarChange = () => {
      logger.debug(`useMatrixAvatarListener: Avatar change event received for ${userId}`);
      onAvatarChange();
    };

    // Set up the listener
    logger.debug(`useMatrixAvatarListener: Setting up listener for ${userId}`);
    handlerRef.current = handleAvatarChange;
    user.on(UserEvent.AvatarUrl, handleAvatarChange);
    listenerSetupRef.current = true;
    userIdRef.current = userId;

    // Cleanup function
    return () => {
      if (handlerRef.current) {
        logger.debug(`useMatrixAvatarListener: Cleaning up listener for ${userId}`);
        user.off(UserEvent.AvatarUrl, handlerRef.current);
      }
      listenerSetupRef.current = false;
      userIdRef.current = null;
      handlerRef.current = null;
    };
  }, [client, userId, onAvatarChange]);
};

export default useMatrixAvatarListener;
